<script setup lang="ts">
import { faCompactDisc } from '@fortawesome/pro-solid-svg-icons';
import { GpSwitch, GpTextInput } from '@gportal/uikit';
import { useImageStore } from 'gpcore-apikit/store/pinia/image';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { AuthenticationType } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import AsyncModal from 'gpcore-uikit/components/new/AsyncModal.vue';
import onModalShow from 'gpcore-uikit/composables/onModalShow';
import { useField, useForm } from 'vee-validate';
import { computed } from 'vue';
import { bool as yupBool, object as yupObject, string as yupString } from 'yup';

const props = defineProps<{ show: boolean }>();

const emit = defineEmits<{
  'update:show': [boolean];
  created: [];
}>();

type FormData = {
  name: string;
  sshAuthEnabled: boolean;
  passwordAuthEnabled: boolean;
};

const { errors, handleSubmit, resetForm } = useForm<FormData>({
  validationSchema: yupObject().shape(
    {
      name: yupString().required('Name is required'),
      sshAuthEnabled: yupBool().when('passwordAuthEnabled', {
        is: (pwAuth) => !pwAuth,
        then: (schema) => schema.isTrue('At least one authentication method must be enabled.'),
      }),
      passwordAuthEnabled: yupBool().when('sshAuthEnabled', {
        is: (sshAuth) => !sshAuth,
        then: (schema) => schema.isTrue('At least one authentication method must be enabled.'),
      }),
    },
    [
      ['sshAuthEnabled', 'passwordAuthEnabled'],
      ['passwordAuthEnabled', 'sshAuthEnabled'],
    ],
  ),
});

const { value: name } = useField<string>('name', undefined, {
  initialValue: '',
});

const { value: sshAuthEnabled } = useField<boolean>('sshAuthEnabled', undefined, {
  initialValue: false,
});

const { value: passwordAuthEnabled } = useField<boolean>('passwordAuthEnabled', undefined, {
  initialValue: false,
});

const imageStore = useImageStore();
const projectStore = useProjectStore();
const isValid = computed(
  () => name.value !== '' && (sshAuthEnabled.value || passwordAuthEnabled.value),
);
const create = handleSubmit(async () => {
  const { projectId } = projectStore;
  const authenticationTypes: Array<AuthenticationType> = [];
  if (sshAuthEnabled.value) {
    authenticationTypes.push(AuthenticationType.SSH);
  }

  if (passwordAuthEnabled.value) {
    authenticationTypes.push(AuthenticationType.PASSWORD);
  }

  return imageStore.createProjectImage(projectId, {
    name: name.value,
    authenticationTypes,
  });
});

onModalShow(props, () => {
  resetForm();
});
</script>

<template>
  <AsyncModal
    :show="show"
    :header-icon="faCompactDisc"
    header-text="Create project image"
    :async-function="create"
    confirm-text="Create"
    :is-valid="isValid"
    @close="emit('update:show', false)"
    @done="emit('created')"
  >
    <template #content>
      <div class="tw-mb-8 tw-px-4">
        <GpTextInput
          v-model="name"
          input-id="create-project-name"
          label="Name"
          class="tw-mb-4"
          name="name"
          :error="errors.name"
        />
        <GpSwitch
          v-model="sshAuthEnabled"
          input-id="create-project-ssh"
          label="SSH key-based authentication"
          class="tw-mb-4"
          name="sshAuthEnabled"
          :error="errors.sshAuthEnabled"
        />
        <GpSwitch
          v-model="passwordAuthEnabled"
          input-id="create-project-ssh"
          label="Password authentication"
          name="passwordAuthEnabled"
          :error="errors.passwordAuthEnabled"
        />
      </div>
    </template>
  </AsyncModal>
</template>

<style scoped></style>
